/**
 * ContractorMyJobs — the crew's side of dispatch: what they are booked on over
 * the next two weeks, with the call time, meeting point and scope.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarClock, MapPin, Clock, ShieldAlert, Loader2 } from "lucide-react";
import { API } from "@/App";

const day = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
  });

export default function ContractorMyJobs({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/crew/my-assignments`, { headers });
      setRows(r.data?.assignments || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <section className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="text-sm text-slate-500 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />Loading your schedule…</p>
    </section>;
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="contractor-my-jobs">
      <h3 className="text-sm font-black uppercase tracking-wider text-slate-800 mb-1 inline-flex items-center gap-2">
        <CalendarClock className="w-4 h-4 text-amber-600" />Your next two weeks
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        Jobs the project manager has dispatched to you. Reply to the dispatch email if you cannot make a call time.
      </p>
      {rows.length === 0 ? (
        <div className="text-center py-8 rounded-lg border border-dashed border-slate-300">
          <p className="text-sm text-slate-500">Nothing scheduled for you right now.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} className="rounded-lg ring-1 ring-slate-200 p-3"
              data-testid={`my-job-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    <span className="inline-block w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: a.color }} />
                    {a.title}
                  </p>
                  <p className="text-xs text-slate-600 mt-1 inline-flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />{day(a.job_date)} · {a.start_time}–{a.end_time}
                    </span>
                    {a.location ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{a.location}
                      </span>
                    ) : null}
                  </p>
                  {a.scope ? (
                    <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{a.scope}</p>
                  ) : null}
                  {a.icra_permit_number ? (
                    <p className="text-[11px] text-red-700 font-bold mt-1 inline-flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" />
                      Under ICRA permit {a.icra_permit_number} — read the door sign before you start
                    </p>
                  ) : null}
                </div>
                <span className="text-[10px] font-black uppercase tracking-wide ring-1 rounded-full px-2 py-0.5 bg-slate-100 text-slate-600 ring-slate-300">
                  {(a.status || "").replace(/_/g, " ")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
